/*
 * <loop-widget> — the custom Loop subscription widget's behaviour (P9).
 *
 * A straight port of the inline IIFE live shipped inside
 * snippets/loop-widget-custom.liquid, rehomed as a deferred custom element
 * (§5: no inline theme script, config via data attributes + a JSON script
 * tag). Function-for-function parity with live, with the two integration
 * points rewritten for the rebuild's Dawn 15.5 machinery:
 *
 *  - DRIVING a variant change: live wrote into Dawn 7's <variant-selects>
 *    all-variants JSON. Dawn 15.5 pickers carry no such JSON, so each tile
 *    brings its own option values (data-loop-option-values) and the element
 *    sets the picker's inputs and dispatches `change` — <variant-selects>
 *    then runs the full pipeline (section re-render, price, media, URL,
 *    availability gate). Without a picker on the page (live's
 *    ballaststoff-pulver template disables it) the fallback writes the
 *    form's [name="id"] directly, swaps the gallery media itself, and
 *    dispatches `variant:change` — exactly live's fallback contract.
 *
 *  - REACTING to a variant change: live listened for its theme's
 *    `ed:variant:change`. The rebuild's authoritative signal is
 *    PUB_SUB_EVENTS.variantChange (product-info.js publishes it after every
 *    swap); the form's own [name="id"] change event is kept as the
 *    belt-and-braces second trigger, as on live.
 *
 * State contract (live's): the subscribe/one-time choice PERSISTS across
 * variant changes; the hidden selling_plan input carries the active Loop
 * plan id in subscribe mode and the empty string in one-time mode, so a
 * no-JS submit — and any submit before this element upgrades — posts a
 * one-time purchase.
 */

if (!customElements.get('loop-widget')) {
  customElements.define(
    'loop-widget',
    class LoopWidget extends HTMLElement {
      connectedCallback() {
        this.formId = this.dataset.formId || '';
        this.sectionId = this.formId ? this.formId.replace(/^product-form-/, '') : '';

        const dataEl = this.querySelector('script[data-loop-plans]');
        this.planData = null;
        if (dataEl) {
          try {
            this.planData = JSON.parse(dataEl.textContent);
          } catch (e) {
            // Log but don't block mode switching — price rendering just
            // falls back to the tile data / variant price (live's contract).
            console.warn('[loop-widget] plan data parse failed', e);
          }
        }

        // Mode switching works whenever the subscribe card is rendered
        // (Loop configured on this product), independently of whether the
        // price-map JSON parsed. Price rendering is gated separately.
        this.hasLoop = !!this.subCard;

        this.state = {
          mode: 'onetime',
          planId: this.planData ? this.planData.defaultPlanId : null,
          variantId: null,
        };

        this.bindEvents();

        this.state.variantId =
          this.currentVariantId() ||
          (this.tiles[0] && this.tiles[0].getAttribute('data-variant-id')) ||
          null;
        this.setMode('onetime');
        this.repaintSubscriptionCard();
      }

      disconnectedCallback() {
        if (this.variantChangeUnsubscriber) this.variantChangeUnsubscriber();
      }

      /* --- Live DOM refs (queried fresh; product-info swaps neighbours) --- */

      get input() {
        return this.querySelector('[data-loop-input]');
      }
      get oneCard() {
        return this.querySelector('[data-loop-card="onetime"]');
      }
      get subCard() {
        return this.querySelector('[data-loop-card="subscribe"]');
      }
      get tiles() {
        return this.querySelectorAll('[data-loop-tile]');
      }

      /* --- Form / section lookups ---------------------------------------- */

      getForm() {
        if (this.formId) {
          const byId = document.getElementById(this.formId);
          if (byId) return byId;
        }
        return this.closest('form[action*="/cart/add"]') || this.closest('form');
      }

      currentVariantId() {
        const form = this.getForm();
        if (!form) return null;
        const el = form.querySelector('[name="id"]');
        return el ? el.value : null;
      }

      getVariantSelector() {
        let variantSelects = null;
        if (this.sectionId) {
          variantSelects = document.querySelector(`variant-selects[data-section="${this.sectionId}"]`);
        }
        return variantSelects || document.querySelector('variant-selects');
      }

      galleryForProduct() {
        return this.sectionId
          ? document.getElementById(`MediaGallery-${this.sectionId}`)
          : document.querySelector('media-gallery');
      }

      /* --- Tile data ------------------------------------------------------ */

      tileForVariant(variantId) {
        const nextId = String(variantId);
        for (const tile of this.tiles) {
          if (tile.getAttribute('data-variant-id') === nextId) return tile;
        }
        return null;
      }

      planDataFromTile(variantId) {
        const tile = this.tileForVariant(variantId);
        if (!tile || !tile.dataset) return null;

        return {
          planId: tile.dataset.loopPlanId || null,
          name: tile.dataset.loopPlanName || '',
          option: tile.dataset.loopPlanOption || tile.dataset.loopPlanName || '',
          description: tile.dataset.loopPlanDescription || '',
          badge: tile.dataset.loopPlanBadge || '',
          price: tile.dataset.loopSubPrice || '',
          compare: tile.dataset.loopSubCompare || '',
          hasCompare: tile.dataset.loopSubHasCompare === 'true',
        };
      }

      /* --- Subscribe-card rendering --------------------------------------- */

      getOrCreateBadge() {
        let badgeEl = this.querySelector('[data-loop-badge]');
        if (badgeEl) return badgeEl;

        const header = this.subCard && this.subCard.querySelector('.loop-card__header');
        if (!header) return null;

        badgeEl = document.createElement('span');
        badgeEl.className = 'loop-card__badge font__body-small-bold';
        badgeEl.setAttribute('data-loop-badge', '');
        header.appendChild(badgeEl);
        return badgeEl;
      }

      renderPlanHeader(planData) {
        if (!planData) return;

        const planLabel = this.querySelector('[data-loop-plan-label]');
        const cardTitle = this.querySelector('[data-loop-card-title]');
        if (planData.name) {
          if (planLabel) planLabel.textContent = planData.option || planData.name;
          if (cardTitle) cardTitle.textContent = planData.name;
        }

        const planDescEl = this.querySelector('[data-loop-sub-description]');
        if (planDescEl) {
          if (planData.description) {
            planDescEl.textContent = planData.description;
            planDescEl.hidden = false;
          } else {
            planDescEl.textContent = '';
            planDescEl.hidden = true;
          }
        }

        const badgeEl = planData.badge ? this.getOrCreateBadge() : this.querySelector('[data-loop-badge]');
        if (badgeEl) {
          if (planData.badge) {
            badgeEl.textContent = planData.badge;
            badgeEl.style.display = '';
          } else {
            badgeEl.textContent = '';
            badgeEl.style.display = 'none';
          }
        }
      }

      planForVariant(variantId, planId) {
        if (!this.planData) return null;
        const v = this.planData.variants[String(variantId)];
        if (v && v[String(planId)]) return v[String(planId)];
        if (v) {
          const keys = Object.keys(v);
          if (keys.length) return v[keys[0]];
        }
        return null;
      }

      renderSubPrice() {
        // Scoped to the subscribe card: the tiles carry a same-named
        // data-loop-sub-price attribute, and an unscoped query would hit the
        // first tile and overwrite its markup.
        const subPrice = this.subCard && this.subCard.querySelector('[data-loop-sub-price]');
        if (!subPrice) return;
        const tileData = this.planDataFromTile(this.state.variantId);
        const alloc =
          tileData && tileData.price ? tileData : this.planForVariant(this.state.variantId, this.state.planId);
        if (!alloc) {
          subPrice.innerHTML = '';
          return;
        }
        let html = `<span class="loop-price">${alloc.price}</span>`;
        if (alloc.hasCompare) {
          html += `<span class="loop-price-strike">${alloc.compare}</span>`;
        }
        subPrice.innerHTML = html;
        this.renderPlanHeader(alloc);
      }

      repaintSubscriptionCard() {
        this.syncPlanForVariant();
        this.renderSubPrice();
      }

      // The deferred repaint covers theme code that finishes its own variant
      // work (product-info's async section swap) after this click.
      repaintSubscriptionCardDeferred() {
        window.requestAnimationFrame(() => this.repaintSubscriptionCard());
        window.setTimeout(() => this.repaintSubscriptionCard(), 50);
      }

      // Pick the selling plan bound to the active variant and update the
      // static plan label + hidden selling_plan input accordingly. Always
      // rewrites state.planId so the variant-index → plan-index mapping wins
      // over stale state — critical when Loop is configured with ONE group
      // holding all N plans applied to all N variants.
      syncPlanForVariant() {
        const variantId = String(this.state.variantId);
        const tileData = this.planDataFromTile(variantId);
        const planMap = this.planData && this.planData.variants ? this.planData.variants[variantId] || {} : {};
        const keys = Object.keys(planMap);

        if (tileData && tileData.planId) {
          this.state.planId = String(tileData.planId);
        } else if (keys.length) {
          const wanted = this.planData.variantDefaultPlan && this.planData.variantDefaultPlan[variantId];
          if (wanted != null && planMap[String(wanted)]) {
            this.state.planId = String(wanted);
          } else {
            this.state.planId = keys[0];
          }
        } else {
          return;
        }

        this.syncInput();
        this.renderPlanHeader(tileData || planMap[this.state.planId]);
      }

      /* --- Mode toggle ----------------------------------------------------- */

      syncInput() {
        const input = this.input;
        if (!input) return;
        input.value = this.state.mode === 'subscribe' && this.hasLoop ? this.state.planId : '';
      }

      setMode(mode) {
        if (mode === 'subscribe' && !this.hasLoop) return;
        this.state.mode = mode;
        this.classList.toggle('loop-widget--onetime', mode === 'onetime');
        this.classList.toggle('loop-widget--subscribe', mode === 'subscribe');
        const oneCard = this.oneCard;
        const subCard = this.subCard;
        if (oneCard) oneCard.classList.toggle('is-selected', mode === 'onetime');
        if (subCard) subCard.classList.toggle('is-selected', mode === 'subscribe');
        const oneRadio = oneCard && oneCard.querySelector('[data-loop-mode="onetime"]');
        if (oneRadio) oneRadio.checked = mode === 'onetime';
        if (subCard) {
          const subRadio = subCard.querySelector('[data-loop-mode="subscribe"]');
          if (subRadio) subRadio.checked = mode === 'subscribe';
        }
        this.syncInput();
      }

      /* --- Variant selection ------------------------------------------------ */

      paintVariantTiles(variantId) {
        const nextId = String(variantId);
        this.tiles.forEach((tile) => {
          const match = tile.getAttribute('data-variant-id') === nextId;
          tile.classList.toggle('is-selected', match);
          const r = tile.querySelector('.loop-variant__radio');
          if (r) r.checked = match;
        });
      }

      // Drive the theme's variant machinery for a tile click.
      // 1) Preferred: set the section's <variant-selects> inputs to the
      //    tile's option values and dispatch `change` — Dawn's pipeline does
      //    the rest (section re-render, price, media, URL, buy button).
      // 2) Fallback (no picker on the page): write the form's [name="id"]
      //    directly and make sure it is enabled so the form submits the
      //    variant; announce via `variant:change` like live.
      setFormVariant(variantId) {
        const variantSelects = this.getVariantSelector();

        if (variantSelects) {
          const tile = this.tileForVariant(variantId);
          let optionValues = null;
          if (tile && tile.dataset.loopOptionValues) {
            try {
              optionValues = JSON.parse(tile.dataset.loopOptionValues);
            } catch (e) {
              /* fall through to the direct update */
            }
          }
          if (optionValues && optionValues.length) {
            const groups = variantSelects.querySelectorAll('fieldset, .product-form__input--dropdown');
            let changedInput = null;
            groups.forEach((group, idx) => {
              const wanted = optionValues[idx];
              if (wanted == null) return;
              const select = group.querySelector('select');
              if (select) {
                if (select.value !== wanted) {
                  select.value = wanted;
                  changedInput = select;
                } else if (!changedInput) {
                  changedInput = select;
                }
                return;
              }
              group.querySelectorAll('input[type="radio"]').forEach((radio) => {
                if (radio.value === wanted) {
                  if (!radio.checked) {
                    radio.checked = true;
                    changedInput = radio;
                  } else if (!changedInput) {
                    changedInput = radio;
                  }
                }
              });
            });
            if (changedInput) {
              changedInput.dispatchEvent(new Event('change', { bubbles: true }));
              return;
            }
          }
        }

        // 2) Fallback — no <variant-selects> on the page (live's
        //    ballaststoff-pulver template: the widget IS the picker).
        const form = this.getForm();
        if (!form) return;
        const el = form.querySelector('[name="id"]');
        if (!el) return;
        if (el.value !== String(variantId)) el.value = variantId;
        el.removeAttribute('disabled');
        el.dispatchEvent(new Event('change', { bubbles: true }));
        document.dispatchEvent(
          new CustomEvent('variant:change', {
            detail: { variantId: variantId, source: 'loop-widget' },
          })
        );
      }

      /* --- Gallery media ---------------------------------------------------- */

      galleryHasMedia(gallery, mediaId) {
        return !!(gallery && mediaId && gallery.querySelector(`[data-media-id="${mediaId}"]`));
      }

      mediaTargetFromRawId(gallery, rawMediaId) {
        if (!gallery || !rawMediaId) return null;
        const exactMediaId = this.sectionId ? `${this.sectionId}-${rawMediaId}` : String(rawMediaId);
        if (this.galleryHasMedia(gallery, exactMediaId)) return exactMediaId;

        const mediaEl = gallery.querySelector(`[data-media-id$="-${rawMediaId}"]`);
        return mediaEl ? mediaEl.getAttribute('data-media-id') : null;
      }

      activateGalleryMedia(gallery, mediaId) {
        if (!gallery || !mediaId || typeof gallery.setActiveMedia !== 'function') return false;
        if (!this.galleryHasMedia(gallery, mediaId)) return false;
        try {
          gallery.setActiveMedia(mediaId, true);
          return true;
        } catch (e) {
          console.warn('[loop-widget] setActiveMedia failed', e);
          return false;
        }
      }

      // Last-resort image swap when the gallery carries no media entry for
      // the variant (live's localized-image case) — live's exact behaviour.
      swapActiveProductImage(tile, gallery) {
        if (!tile || !tile.dataset || !tile.dataset.loopImageSrc || !gallery) return false;

        const activeMedia =
          gallery.querySelector('.product__media-item.is-active') ||
          gallery.querySelector('.product__media-item[data-media-id]');
        if (!activeMedia) return false;

        const image = activeMedia.querySelector('.product__media img') || activeMedia.querySelector('img');
        if (!image) return false;

        image.src = tile.dataset.loopImageSrc;
        image.srcset = tile.dataset.loopImageSrc;
        if (tile.dataset.loopImageAlt) image.alt = tile.dataset.loopImageAlt;

        const modal = this.sectionId ? document.getElementById(`ProductModal-${this.sectionId}`) : null;
        const modalImage = modal && modal.querySelector('.product-media-modal__content img');
        if (modalImage) {
          modalImage.src = tile.dataset.loopImageSrc;
          modalImage.srcset = tile.dataset.loopImageSrc;
          if (tile.dataset.loopImageAlt) modalImage.alt = tile.dataset.loopImageAlt;
        }

        activeMedia.style.display = '';
        activeMedia.classList.add('is-active');
        activeMedia.classList.remove('product__media-item--variant-mobile-hide');
        return true;
      }

      setProductMediaForVariant(variantId) {
        const tile = this.tileForVariant(variantId);
        const gallery = this.galleryForProduct();
        if (!gallery) return;

        let mediaId = null;
        if (tile && tile.dataset && tile.dataset.loopMediaId) {
          mediaId = this.mediaTargetFromRawId(gallery, tile.dataset.loopMediaId);
        }

        if (!mediaId) {
          mediaId = this.mediaTargetFromGalleryVariant(gallery, String(variantId));
        }

        if (this.activateGalleryMedia(gallery, mediaId)) return;
        this.swapActiveProductImage(tile, gallery);
      }

      mediaTargetFromGalleryVariant(gallery, variantId) {
        if (!gallery || !variantId) return null;
        const thumbnail = gallery.querySelector(`[data-variant-id="${variantId}"][data-target]`);
        return thumbnail ? thumbnail.getAttribute('data-target') : null;
      }

      setProductMediaForVariantDeferred(variantId) {
        window.requestAnimationFrame(() => this.setProductMediaForVariant(variantId));
        window.setTimeout(() => this.setProductMediaForVariant(variantId), 50);
      }

      setVariant(variantId, fromForm) {
        if (!variantId) return;
        const nextId = String(variantId);
        const changed = nextId !== this.state.variantId;
        this.state.variantId = nextId;

        // Always re-paint tile selection (covers re-entry from external events).
        this.paintVariantTiles(nextId);
        this.setProductMediaForVariant(nextId);

        // Drive the theme's variant pipeline — wrapped so a theme error can't
        // kill this widget's own UI sync (live's guard).
        if (!fromForm && changed) {
          try {
            this.setFormVariant(nextId);
          } catch (e) {
            console.warn('[loop-widget] setFormVariant failed', e);
          }

          // setFormVariant can synchronously re-enter setVariant() through the
          // picker's change pipeline. The clicked tile remains the source of
          // truth for this widget's plan/header repaint.
          this.state.variantId = nextId;
          this.paintVariantTiles(nextId);
          this.setProductMediaForVariant(nextId);
        }

        this.repaintSubscriptionCard();
        this.repaintSubscriptionCardDeferred();
        this.setProductMediaForVariantDeferred(nextId);
      }

      /* --- Event wiring ------------------------------------------------------ */

      bindEvents() {
        // Mode toggle uses two redundant triggers (live's):
        //   1. `change` on the purchase-type radios (keyboard, a11y, default)
        //   2. `click` on the card headers (covers any browser where the
        //      label→radio click-through misfires)
        this.querySelectorAll('input[data-loop-mode]').forEach((r) => {
          r.addEventListener('change', () => {
            if (r.checked) this.setMode(r.value);
          });
        });

        const oneHeader = this.oneCard && this.oneCard.querySelector('.loop-card__header');
        if (oneHeader) {
          oneHeader.addEventListener('click', () => this.setMode('onetime'));
        }
        const subCard = this.subCard;
        if (subCard) {
          const subHeader = subCard.querySelector('.loop-card__header');
          if (subHeader) {
            subHeader.addEventListener('click', () => this.setMode('subscribe'));
          }
          // Clicking into the expanded area (label, price, benefits) also
          // forces subscribe mode — the user may jump straight there.
          const subExpanded = subCard.querySelector('[data-loop-expanded]');
          if (subExpanded) {
            subExpanded.addEventListener('pointerdown', () => this.setMode('subscribe'));
          }
        }

        // Variant tile selection: radio change + tile click as backup.
        // Deliberately does NOT force one-time mode — the subscribe choice
        // persists across variant changes so selling_plan stays populated.
        this.querySelectorAll('.loop-variant__radio').forEach((r) => {
          r.addEventListener('change', () => {
            if (!r.checked || r.disabled) return;
            this.setVariant(r.value);
          });
        });
        this.tiles.forEach((tile) => {
          tile.addEventListener('click', () => {
            if (tile.classList.contains('is-unavailable')) return;
            this.setVariant(tile.getAttribute('data-variant-id'));
          });
        });

        // React to external variant changes. The authoritative signal is the
        // rebuild's variantChange publish (product-info.js fires it after
        // every swap); the form's own [name="id"] change is the second
        // trigger, exactly as on live.
        const form = this.getForm();
        if (form) {
          form.addEventListener('change', (e) => {
            if (e.target && e.target.name === 'id') {
              this.setVariant(e.target.value, true);
            }
          });
        }
        document.addEventListener('variant:change', (e) => {
          if (e.detail && e.detail.source === 'loop-widget') return;
          const next = this.currentVariantId();
          if (next) this.setVariant(next, true);
        });
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
            if (this.sectionId && data.sectionId && data.sectionId !== this.sectionId) return;
            if (data.variant && data.variant.id) this.setVariant(data.variant.id, true);
          });
        }
      }
    }
  );
}
